/**
 * Logo Asset Management Service
 * 
 * Handles caching, retrieval, and offline persistence of logo assets
 * using IndexedDB blob storage for the authenticated app areas.
 */

import { offlineDB } from '../indexeddb/database';
import { STORES, type StaticAssetOffline } from '../indexeddb/schema';

/**
 * Logo configuration for authenticated app areas
 */
export const LOGO_CONFIG = {
  SIDEBAR_LOGO: {
    url: '/lovable-uploads/309c4bff-08a2-44a3-9eb8-307bc935b2a8.png',
    type: 'logo' as const,
    key: 'sidebar_logo'
  },
  MOBILE_HEADER_LOGO: {
    url: '/lovable-uploads/b9e6fa8f-f749-4515-b67c-d28715d804c5.png',
    type: 'logo' as const,
    key: 'mobile_header_logo'
  },
  USER_AVATAR: {
    url: '/lovable-uploads/aa1f7238-2168-4dae-8ddc-d3d93b7cc03d.png',
    type: 'icon' as const,
    key: 'user_avatar'
  }
} as const;

/**
 * Cache configuration
 */
export interface CacheConfig {
  ttl: number; // Time to live in milliseconds (default: 7 days)
  maxRetries: number; // Maximum retry attempts for failed downloads
  timeout: number; // Download timeout in milliseconds
}

const DEFAULT_CACHE_CONFIG: CacheConfig = {
  ttl: 7 * 24 * 60 * 60 * 1000, // 7 days
  maxRetries: 3,
  timeout: 10000 // 10 seconds
};

/**
 * Logo Asset Manager Class
 */
export class LogoAssetManager {
  private config: CacheConfig;

  constructor(config: Partial<CacheConfig> = {}) {
    this.config = { ...DEFAULT_CACHE_CONFIG, ...config };
  }

  /**
   * Cache all logos for authenticated app areas
   */
  async cacheAllLogos(): Promise<{ success: boolean; errors: string[] }> {
    const errors: string[] = [];
    let successCount = 0;

    const logos = Object.values(LOGO_CONFIG);

    for (const logo of logos) {
      try {
        const cached = await this.cacheLogo(logo.url, logo.type, logo.key);
        if (cached) {
          successCount++;
        } else {
          errors.push(`Failed to cache logo: ${logo.key}`);
        }
      } catch (error) {
        const errorMsg = `Error caching ${logo.key}: ${error instanceof Error ? error.message : 'Unknown error'}`;
        errors.push(errorMsg);
        console.error('❌', errorMsg);
      }
    }

    const success = successCount === logos.length;

    return { success, errors };
  }

  /**
   * Force recache all logos (useful for debugging)
   */
  async forceCacheAllLogos(): Promise<{ success: boolean; errors: string[] }> {
    // Clear existing cache first
    const logos = Object.values(LOGO_CONFIG);
    for (const logo of logos) {
      try {
        await this.removeCachedAsset(logo.url);
      } catch (error) {
        console.warn(`⚠️ Failed to clear cache for ${logo.key}:`, error);
      }
    }

    // Now cache all logos fresh
    return this.cacheAllLogos();
  }

  /**
   * Cache a single logo asset
   */
  async cacheLogo(
    url: string,
    assetType: 'logo' | 'image' | 'icon',
    cacheKey: string
  ): Promise<boolean> {
    try {
      // Check if already cached and not expired
      const existing = await this.getCachedAsset(url);
      if (existing && !this.isExpired(existing)) {

        return true;
      }

      // Download the logo
      const blob = await this.downloadAsset(url);
      if (!blob) {
        throw new Error(`Failed to download asset from ${url}`);
      }

      // Create cache entry
      const now = new Date().toISOString();
      const expiresAt = new Date(Date.now() + this.config.ttl).toISOString();

      const assetRecord: StaticAssetOffline = {
        id: url, // Use URL as primary key
        asset_type: assetType,
        blob_data: blob,
        mime_type: blob.type,
        file_size: blob.size,
        cached_at: now,
        expires_at: expiresAt,
        last_accessed: now,
        original_url: url,
        cache_key: cacheKey,
        updated_at: now, // Required for BaseOperations compatibility
        _cache_version: 1,
        _last_validated: now
      };

      // Store in IndexedDB
      await offlineDB.put(STORES.STATIC_ASSETS, assetRecord);
      

      return true;

    } catch (error) {
      console.error(`❌ Failed to cache logo ${cacheKey}:`, error);
      return false;
    }
  }

  /**
   * Get cached logo as data URL (works better in Capacitor than blob URLs)
   */
  async getCachedLogoUrl(url: string): Promise<string | null> {
    try {
      const asset = await this.getCachedAsset(url);

      if (!asset) {

        return null;
      }

      if (this.isExpired(asset)) {

        // Optionally remove expired asset
        await this.removeCachedAsset(url);
        return null;
      }

      // Update last accessed time
      await this.updateLastAccessed(url);

      // Convert blob to data URL for better Capacitor compatibility
      const dataUrl = await this.blobToDataUrl(asset.blob_data);


      return dataUrl;

    } catch (error) {
      console.error(`❌ Error retrieving cached logo for ${url}:`, error);
      return null;
    }
  }

  /**
   * Check if a logo is cached and valid
   */
  async isLogoCached(url: string): Promise<boolean> {
    try {
      const asset = await this.getCachedAsset(url);
      return asset !== null && !this.isExpired(asset);
    } catch (error) {
      console.error(`❌ Error checking cache status for ${url}:`, error);
      return false;
    }
  }

  /**
   * Clear expired logos from cache
   */
  async clearExpiredLogos(): Promise<number> {
    try {
      const allAssets = await offlineDB.getAll(STORES.STATIC_ASSETS) as StaticAssetOffline[];
      let removedCount = 0;

      for (const asset of allAssets) {
        if (this.isExpired(asset)) {
          await offlineDB.delete(STORES.STATIC_ASSETS, asset.id);
          removedCount++;

        }
      }



      return removedCount;
    } catch (error) {
      console.error('❌ Error clearing expired logos:', error);
      return 0;
    }
  }

  /**
   * Cache technique images in background
   */
  async cacheTechniqueImages(imageUrls: string[]): Promise<void> {
    if (!navigator.onLine) {
      console.log('📱 Offline, skipping technique image caching');
      return;
    }



    // Process images in batches to avoid overwhelming the network
    const batchSize = 5;
    let cached = 0;
    let failed = 0;

    for (let i = 0; i < imageUrls.length; i += batchSize) {
      const batch = imageUrls.slice(i, i + batchSize);

      const batchPromises = batch.map(async (url) => {
        try {
          const success = await this.cacheLogo(url, 'image', `technique-${url}`);
          if (success) {
            cached++;
          } else {
            failed++;
          }
        } catch (error) {
          console.warn(`Failed to cache technique image ${url}:`, error);
          failed++;
        }
      });

      await Promise.allSettled(batchPromises);

      // Small delay between batches to prevent overwhelming the network
      if (i + batchSize < imageUrls.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }


  }

  /**
   * Get cache statistics
   */
  async getCacheStats(): Promise<{
    totalAssets: number;
    totalSize: number;
    expiredAssets: number;
    logoAssets: number;
  }> {
    try {
      const allAssets = await offlineDB.getAll(STORES.STATIC_ASSETS) as StaticAssetOffline[];

      const stats = {
        totalAssets: allAssets.length,
        totalSize: allAssets.reduce((sum, asset) => sum + asset.file_size, 0),
        expiredAssets: allAssets.filter(asset => this.isExpired(asset)).length,
        logoAssets: allAssets.filter(asset => asset.asset_type === 'logo').length
      };

      return stats;
    } catch (error) {
      console.error('❌ Error getting cache stats:', error);
      return { totalAssets: 0, totalSize: 0, expiredAssets: 0, logoAssets: 0 };
    }
  }

  /**
   * Validate cache integrity for all logos
   */
  async validateCache(): Promise<{
    isValid: boolean;
    issues: string[];
    recommendations: string[];
  }> {
    const issues: string[] = [];
    const recommendations: string[] = [];

    try {
      // Check if all required logos are cached
      const requiredLogos = Object.values(LOGO_CONFIG);

      for (const logo of requiredLogos) {
        const isCached = await this.isLogoCached(logo.url);
        if (!isCached) {
          issues.push(`Missing cached logo: ${logo.key}`);
          recommendations.push(`Cache ${logo.key} for offline functionality`);
        }
      }

      // Check for expired assets
      const expiredCount = await this.getExpiredAssetCount();
      if (expiredCount > 0) {
        issues.push(`${expiredCount} expired asset(s) found`);
        recommendations.push('Run clearExpiredLogos() to clean up expired assets');
      }

      // Check cache size
      const stats = await this.getCacheStats();
      const maxSizeBytes = 50 * 1024 * 1024; // 50MB limit
      if (stats.totalSize > maxSizeBytes) {
        issues.push(`Cache size (${Math.round(stats.totalSize / 1024 / 1024)}MB) exceeds recommended limit`);
        recommendations.push('Consider clearing expired assets or reducing cache TTL');
      }

      const isValid = issues.length === 0;

      if (isValid) {
        console.log('✅ Logo cache validation passed');
      } else {
        console.warn('⚠️ Logo cache validation found issues:', issues);
      }

      return { isValid, issues, recommendations };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ Cache validation failed:', errorMessage);

      return {
        isValid: false,
        issues: [`Cache validation failed: ${errorMessage}`],
        recommendations: ['Check IndexedDB connectivity and try again']
      };
    }
  }

  /**
   * Repair cache by re-downloading missing or corrupted logos
   */
  async repairCache(): Promise<{
    success: boolean;
    repaired: string[];
    errors: string[];
  }> {
    const repaired: string[] = [];
    const errors: string[] = [];

    try {
      console.log('🔧 Starting cache repair...');

      // Validate current cache state
      const validation = await this.validateCache();

      if (validation.isValid) {
        console.log('✅ Cache is already valid, no repair needed');
        return { success: true, repaired: [], errors: [] };
      }

      // Clear expired assets first
      const expiredCount = await this.clearExpiredLogos();
      if (expiredCount > 0) {
        repaired.push(`Cleared ${expiredCount} expired asset(s)`);
      }

      // Re-cache missing logos
      const requiredLogos = Object.values(LOGO_CONFIG);

      for (const logo of requiredLogos) {
        const isCached = await this.isLogoCached(logo.url);
        if (!isCached) {
          try {
            const cached = await this.cacheLogo(logo.url, logo.type, logo.key);
            if (cached) {
              repaired.push(`Re-cached logo: ${logo.key}`);
            } else {
              errors.push(`Failed to re-cache logo: ${logo.key}`);
            }
          } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            errors.push(`Error re-caching ${logo.key}: ${errorMsg}`);
          }
        }
      }

      const success = errors.length === 0;

      if (success) {
        console.log(`✅ Cache repair completed successfully. Repaired: ${repaired.length} items`);
      } else {
        console.warn(`⚠️ Cache repair completed with errors. Repaired: ${repaired.length}, Errors: ${errors.length}`);
      }

      return { success, repaired, errors };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('❌ Cache repair failed:', errorMessage);

      return {
        success: false,
        repaired,
        errors: [`Cache repair failed: ${errorMessage}`]
      };
    }
  }

  /**
   * Get count of expired assets
   */
  private async getExpiredAssetCount(): Promise<number> {
    try {
      const allAssets = await offlineDB.getAll(STORES.STATIC_ASSETS) as StaticAssetOffline[];
      return allAssets.filter(asset => this.isExpired(asset)).length;
    } catch (error) {
      console.error('❌ Error getting expired asset count:', error);
      return 0;
    }
  }

  // Private helper methods

  private async getCachedAsset(url: string): Promise<StaticAssetOffline | null> {
    try {
      return await offlineDB.get(STORES.STATIC_ASSETS, url) as StaticAssetOffline | null;
    } catch (error) {
      console.error(`❌ Error getting cached asset for ${url}:`, error);
      return null;
    }
  }

  private isExpired(asset: StaticAssetOffline): boolean {
    return new Date(asset.expires_at) < new Date();
  }

  private async downloadAsset(url: string): Promise<Blob | null> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);

      const response = await fetch(url, {
        signal: controller.signal,
        cache: 'no-cache' // Always fetch fresh for caching
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const blob = await response.blob();
      
      // Validate it's an image
      if (!blob.type.startsWith('image/')) {
        throw new Error(`Invalid content type: ${blob.type}`);
      }

      return blob;

    } catch (error) {
      console.error(`❌ Failed to download asset from ${url}:`, error);
      return null;
    }
  }

  private async updateLastAccessed(url: string): Promise<void> {
    try {
      const asset = await this.getCachedAsset(url);
      if (asset) {
        asset.last_accessed = new Date().toISOString();
        await offlineDB.put(STORES.STATIC_ASSETS, asset);
      }
    } catch (error) {
      console.error(`❌ Error updating last accessed for ${url}:`, error);
    }
  }

  private async removeCachedAsset(url: string): Promise<void> {
    try {
      await offlineDB.delete(STORES.STATIC_ASSETS, url);
    } catch (error) {
      console.error(`❌ Error removing cached asset for ${url}:`, error);
    }
  }

  /**
   * Convert blob to data URL for better Capacitor compatibility
   */
  private async blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to convert blob to data URL'));
      reader.readAsDataURL(blob);
    });
  }
}

// Export singleton instance
export const logoAssetManager = new LogoAssetManager();
